import CustomCollapsible from "@/components/ui/collapsibleCard/collapsible-card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue,
  SelectItem,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Control,
  Controller,
  FieldErrors,
  UseFormGetValues,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
  UseFormTrigger,
} from "react-hook-form";
import { RootState } from "@/redux/store";
import { useSelector } from "react-redux";
import { Product, ProductType } from "@/types/system-setting/product";
import { status } from "@/lib/constants/global";
import { useGetIncomeCategoryQuery } from "@/services/incomeCategoryApi";
import { useGetTaxGroupQuery } from "@/services/salesTaxApi";
import { useMemo, useState, useEffect } from "react";
import Combobox from "@/components/ui/common/combobox";

interface ProductPriceSectionProp {
  control: Control<Product>;
  errors: FieldErrors<Product>;
  watch: UseFormWatch<Product>;
  setValue: UseFormSetValue<Product>;
  getValues: UseFormGetValues<Product>;
  register: UseFormRegister<Product>;
  trigger: UseFormTrigger<Product>;
}

const ProductPriceSection = ({
  control,
  errors,
  watch,
  setValue,
  getValues,
  register,
  trigger,
}: ProductPriceSectionProp) => {
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;
  const { data: incomeCategoryData } = useGetIncomeCategoryQuery(
    { org_id: orgId, query: "sort_key=id&sort_order=desc&status=active" },
    {
      skip: !orgId,
    });


  const { data: taxGroupData } = useGetTaxGroupQuery({ query: "", org_id: orgId });

  const watcher = watch();

  // Hide income category and tax group when product type is Wallet
  useEffect(() => {
    if (watcher.type === ProductType.Wallet) {
      setValue("income_category_id", undefined);
      setValue("tax_group_id", undefined);
    }
  }, [watcher.type, setValue]);

  const incomeCatOptions = useMemo(() => {
    return Array.isArray(incomeCategoryData?.data)
      ? incomeCategoryData?.data.map((incomeCat) => ({
        ...incomeCat,
        label: incomeCat?.name || "",
        value: incomeCat?.id?.toString() || "",
      }))
      : [];
  }, [incomeCategoryData]);

  const taxGroupOptions = useMemo(() => {
    return Array.isArray(taxGroupData?.data)
      ? taxGroupData?.data.map((taxGroup) => ({
        ...taxGroup,
        label: taxGroup?.name || "",
        value: taxGroup?.id?.toString() || "",
      }))
      : [];
  }, [taxGroupData]);

  return (
    <div className="rounded-2xl bg-white">
      <CustomCollapsible
        title="Price Details"
        isOpenDefault={true}
        hideToggle={false}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

          {/* <div>
            <p className="text-sm font-medium mb-1.5">
              Product Type <span className="text-red-500">*</span>
            </p>
            <Controller
              control={control}
              name="type"
              rules={{ required: "Required" }}
              defaultValue={watcher.type}
              render={({ field }) => (
                <Select onValueChange={field.onChange} value={field.value}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select Product Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ProductType.Wallet}>Wallet</SelectItem>
                    <SelectItem value={ProductType.OtherProduct}>Other Product</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
            {errors.type?.message && (
              <p className="text-red-500 text-xs mt-1">
                {errors.type.message.toString()}
              </p>
            )}
          </div> */}

          {watcher.type !== ProductType.Wallet && (
            <div>
              <p className="text-sm font-medium mb-1.5">
                Cost<span className="text-red-500">*</span>
              </p>
              <Controller
                control={control}
                name="cost"
                rules={{
                  required: "Required",
                  pattern: {
                    value: /^\d+$/,
                    message: "Only numbers are allowed",
                  },
                }}
                render={({ field }) => (
                  <Input
                    type="number"
                    className="w-full h-9"
                    placeholder="Enter Product Cost"
                    {...field}
                    onChange={(e) => {
                      if (/^\d*$/.test(e.target.value)) {
                        field.onChange(e);
                        trigger("price");
                      }
                    }}
                  />
                )}
              />
              {errors.cost?.message && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.cost.message.toString()}
                </p>
              )}
            </div>
          )}


          {watcher.type == ProductType.Wallet && <div>
            <p className="text-sm font-medium mb-1.5">
              Flexible Price <span className="text-red-500">*</span>
            </p>
            <Controller
              control={control}
              name="flexible_price"
              render={({ field }) => (
                <div className="flex gap-2 h-fit my-3 items-center">
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </div>
              )}
            />
          </div>}
          {!watcher.flexible_price && (
            <div>
              <p className="text-sm font-medium mb-1.5">
                Selling Price <span className="text-red-500">*</span>
              </p>
              <Controller
                control={control}
                name="price"
                rules={{
                  required: "Required",
                  pattern: {
                    value: /^\d+$/,
                    message: "Only numbers are allowed",
                  },
                  validate: (value) => {
                    if (Number(value) === 0) {
                      return "Price cannot be set to zero";
                    }
                    const cost = watch("cost");
                    if (!cost) return true;
                    if (Number(value) < Number(cost)) {
                      return "Price cannot be less than the cost";
                    }
                    return true;
                  }
                }}
                render={({ field }) => (
                  <Input
                    type="number"
                    className="w-full h-9"
                    placeholder="Enter Product Price"
                    {...field}
                    onChange={(e) => {
                      if (/^\d*$/.test(e.target.value)) {
                        field.onChange(e);
                      }
                    }}
                  />
                )}
              />
              {errors.price?.message && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.price.message.toString()}
                </p>
              )}
            </div>
          )}

          {watcher.type !== ProductType.Wallet && (
            <>
              <div>
                <p className="text-sm font-medium mb-1.5">
                  Manage Inventory <span className="text-red-500">*</span>
                </p>
                <Controller
                  control={control}
                  name="manage_inventory"
                  render={({ field }) => (
                    <div className="flex gap-2 h-fit my-3 items-center">
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </div>
                  )}
                />
                {errors.manage_inventory?.message && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.manage_inventory.message.toString()}
                  </p>
                )}
              </div>

              <div>
                <p className="text-sm font-medium mb-1.5">
                  Inventory<span className="text-red-500">*</span>
                </p>
                <Controller
                  control={control}
                  name="quantity"
                  rules={{
                    required: "Required",
                    pattern: {
                      value: /^\d+$/,
                      message: "Only numbers are allowed",

                    },
                    max: {
                      value: 10000,
                      message: "Quantity cannot be greater than 10000",
                    }
                  }}
                  render={({ field }) => (
                    <Input
                      type="number"
                      className="w-full h-9"
                      placeholder="Enter Product Quantity"
                      {...field}
                      onChange={(e) => {
                        if (/^\d*$/.test(e.target.value)) {
                          field.onChange(e);
                        }
                      }}
                    />
                  )}
                />
                {errors.quantity?.message && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.quantity.message.toString()}
                  </p>
                )}
              </div>
            </>
          )}

          {watcher.type !== ProductType.Wallet && (
            <>
              <div>
                <p className="text-sm font-medium mb-1.5">
                  Income Category <span className="text-red-500">*</span>
                </p>
                <Controller
                  name="income_category_id"
                  rules={{ required: watcher.type == ProductType.OtherProduct && "Required" }}
                  control={control}
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <>
                      <Combobox
                        list={incomeCatOptions}
                        defaultValue={
                          incomeCatOptions?.find(
                            (option) => option.value === value?.toString()
                          )?.label || ""
                        }
                        setFilter={(selectedLabel: string) => {
                          const selectedOption = incomeCatOptions?.find(
                            (option) => option.label === selectedLabel
                          );
                          onChange(
                            selectedOption
                              ? Number(selectedOption.value)
                              : undefined
                          );
                        }}
                        label="Income Category"
                        showLabel={false}
                        className="h-9"
                      />
                      {error && (
                        <span className="text-red-500 text-xs mt-[5px]">
                          {error.message}
                        </span>
                      )}
                    </>
                  )}
                />
              </div>

              <div>
                <p className="text-sm font-medium mb-1.5">
                  Tax Group <span className="text-red-500">*</span>
                </p>
                <Controller
                  name="tax_group_id"
                  rules={{ required: watcher.type == ProductType.OtherProduct && "Required" }}
                  control={control}
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <>
                      <Combobox
                        list={taxGroupOptions}
                        defaultValue={
                          taxGroupOptions?.find(
                            (option) => option.value === value?.toString()
                          )?.label || ""
                        }
                        setFilter={(selectedLabel: string) => {
                          const selectedOption = taxGroupOptions?.find(
                            (option) => option.label === selectedLabel
                          );
                          onChange(
                            selectedOption
                              ? Number(selectedOption.value)
                              : undefined
                          );
                        }}
                        label="Tax Group"
                        showLabel={false}
                        className="h-9"
                      />
                      {error && (
                        <span className="text-red-500 text-xs mt-[5px]">
                          {error.message}
                        </span>
                      )}
                    </>
                  )}
                />
              </div>
            </>
          )}


          <div>
            <p className="text-sm font-medium mb-1.5">
              Status <span className="text-red-500">*</span>
            </p>
            <Controller
              name="status"
              rules={{ required: "Required" }}
              control={control}
              render={({
                field: { onChange, value, onBlur },
                fieldState: { invalid, error },
              }) => {
                const statusLabel = status.filter((r) => r.value == value)[0];
                return (
                  <Select
                    onValueChange={(value) => onChange(value)}
                    defaultValue={value}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue
                        placeholder="Select status"
                        className="text-gray-400 "
                      >
                        <span className="flex gap-2 items-center">
                          <span
                            className={`${statusLabel?.color} rounded-[50%] w-4 h-4`}
                          ></span>
                          <span>{statusLabel?.label}</span>
                        </span>
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {status.map((item) => (
                        <SelectItem key={item.value} value={item.value}>
                          {item.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                );
              }}
            />
            {errors.status?.message && (
              <p className="text-red-500 text-xs mt-1">
                {errors.status.message.toString()}
              </p>
            )}
          </div>
        </div>
      </CustomCollapsible>
    </div>
  );
};

export default ProductPriceSection;